import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell,
} from "recharts";
import { getSlides } from "@/lib/analyticsApi";
import { formatError } from "@/lib/api";
import {
  NEON, Card, Loading, Empty, Table, Th, Td, fmtMs, fmtSec, fmtNum,
  TOOLTIP_STYLE, TOOLTIP_ITEM, TOOLTIP_LABEL,
} from "./shared";

const AXIS = { stroke: "rgba(255,255,255,0.35)", fontSize: 11, fontFamily: "'JetBrains Mono', monospace" };

/* Color-scale a cell from dark (cold) to neon (hot) based on its share of the
   max dwell across the grid. */
const cellStyle = (ms, max) => {
  if (!ms) return { background: "rgba(255,255,255,0.03)", color: "rgba(255,255,255,0.3)" };
  const t = max > 0 ? Math.min(1, ms / max) : 0;
  // interpolate rgba alpha of neon green over a dark base
  const alpha = 0.12 + t * 0.78;
  return {
    background: `rgba(200,255,42,${alpha.toFixed(3)})`,
    color: t > 0.45 ? "#060606" : "rgba(255,255,255,0.85)",
    fontWeight: t > 0.45 ? 700 : 500,
  };
};

const SlideDeckView = ({ days }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    getSlides(days)
      .then((d) => { if (alive) setData(d); })
      .catch((e) => toast.error(formatError(e?.response?.data?.detail, "Failed to load slide analytics.")))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [days]);

  const slides = useMemo(() => data?.slides || [], [data]);
  const byVisitor = useMemo(() => data?.by_visitor || [], [data]);

  const chartData = useMemo(
    () => slides.map((s) => ({
      slide: s.slide_n || String((s.slide_index ?? 0) + 1),
      index: s.slide_index ?? 0,
      avgSec: +((s.avg_ms || 0) / 1000).toFixed(1),
      avg_ms: s.avg_ms || 0,
    })),
    [slides]
  );

  // Max per-cell dwell for color scaling the per-visitor grid.
  const maxCell = useMemo(() => {
    let m = 0;
    for (const v of byVisitor) for (const c of (v.per_slide || [])) m = Math.max(m, c.duration_ms || 0);
    return m;
  }, [byVisitor]);

  // Stable, sorted slide-index columns for the grid header.
  const slideCols = useMemo(() => {
    if (slides.length) return slides.map((s) => ({ index: s.slide_index ?? 0, n: s.slide_n }));
    const set = new Set();
    byVisitor.forEach((v) => (v.per_slide || []).forEach((c) => set.add(c.slide_index)));
    return [...set].sort((a, b) => a - b).map((index) => ({ index, n: String(index + 1).padStart(2, "0") }));
  }, [slides, byVisitor]);

  if (loading) return <Loading label="Loading slide deck analytics…" />;
  if (slides.length === 0 && byVisitor.length === 0) {
    return <Empty label="No pitch-deck activity yet. Slide dwell is captured on the Pitch Deck page." />;
  }

  return (
    <div className="space-y-6">
      <Card title="Average Time Per Slide">
        {chartData.length === 0 ? (
          <Empty label="No slide dwell recorded." />
        ) : (
          <div style={{ width: "100%", height: 300 }}>
            <ResponsiveContainer>
              <BarChart data={chartData} margin={{ top: 10, right: 10, left: -18, bottom: 0 }}>
                <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                <XAxis dataKey="slide" tick={AXIS} tickLine={false} axisLine={false} />
                <YAxis tick={AXIS} tickLine={false} axisLine={false} width={42}
                  label={{ value: "sec", angle: -90, position: "insideLeft", fill: "rgba(255,255,255,0.4)", fontSize: 10 }} />
                <Tooltip
                  cursor={{ fill: "rgba(255,255,255,0.05)" }}
                  contentStyle={TOOLTIP_STYLE} itemStyle={TOOLTIP_ITEM} labelStyle={TOOLTIP_LABEL}
                  formatter={(val) => [`${val}s`, "Avg time"]}
                  labelFormatter={(l) => `Slide ${l}`}
                />
                <Bar dataKey="avgSec" radius={[4, 4, 0, 0]}>
                  {chartData.map((_, i) => (
                    <Cell key={i} fill={i % 2 === 0 ? NEON : "#2ad6ff"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      <Card title="Per-Slide Engagement">
        {slides.length === 0 ? (
          <Empty label="No slide data." />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Slide</Th>
                <Th>Views</Th>
                <Th>Uniques</Th>
                <Th>Avg Time</Th>
                <Th>Total Time</Th>
              </tr>
            </thead>
            <tbody>
              {slides.map((s) => (
                <tr key={s.slide_index}>
                  <Td className="font-mono" style={{ color: NEON }}>
                    {s.slide_n || String((s.slide_index ?? 0) + 1).padStart(2, "0")}
                  </Td>
                  <Td className="tabular-nums">{fmtNum(s.views)}</Td>
                  <Td className="tabular-nums">{fmtNum(s.uniques)}</Td>
                  <Td className="tabular-nums">{fmtSec(s.avg_ms)}</Td>
                  <Td className="tabular-nums">{fmtMs(s.total_ms)}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      <Card title="Where Each Visitor Spends Time">
        {byVisitor.length === 0 ? (
          <Empty label="No per-visitor slide data yet." />
        ) : (
          <>
            <div className="text-[10px] tracking-[0.22em] uppercase text-white/45 font-bold mb-3">
              Seconds spent per slide · brighter = longer dwell
            </div>
            <div className="overflow-x-auto -mx-1">
              <table className="border-collapse text-[12px]">
                <thead>
                  <tr>
                    <Th className="sticky left-0 bg-[#0d0d0d] z-10">Visitor</Th>
                    {slideCols.map((c) => (
                      <th
                        key={c.index}
                        className="text-center text-[10px] text-white/45 font-bold py-2 px-1.5 font-mono"
                      >
                        {c.n || String(c.index + 1).padStart(2, "0")}
                      </th>
                    ))}
                    <Th className="text-center">Total</Th>
                  </tr>
                </thead>
                <tbody>
                  {byVisitor.map((v) => {
                    const byIdx = {};
                    let total = 0;
                    (v.per_slide || []).forEach((c) => {
                      byIdx[c.slide_index] = (byIdx[c.slide_index] || 0) + (c.duration_ms || 0);
                      total += c.duration_ms || 0;
                    });
                    const who = [v.ip_city, v.ip_country].filter(Boolean).join(", ");
                    return (
                      <tr key={v.vid}>
                        <td className="py-1.5 px-3 border-t border-white/8 whitespace-nowrap sticky left-0 bg-[#0d0d0d] z-10">
                          <span className="font-mono text-white/70">{(v.vid || "").slice(0, 8)}</span>
                          {(who || v.browser) && (
                            <span className="text-white/40 ml-2">{who || v.browser}</span>
                          )}
                        </td>
                        {slideCols.map((c) => {
                          const ms = byIdx[c.index] || 0;
                          return (
                            <td
                              key={c.index}
                              className="text-center py-1.5 px-1.5 border-t border-white/8 tabular-nums"
                              style={cellStyle(ms, maxCell)}
                              title={`Slide ${c.n} · ${fmtSec(ms)}`}
                            >
                              {ms ? Math.round(ms / 1000) : ""}
                            </td>
                          );
                        })}
                        <td className="text-center py-1.5 px-3 border-t border-white/8 tabular-nums text-white/70">
                          {fmtMs(total)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Card>
    </div>
  );
};

export default SlideDeckView;
